import React, { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Star, Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const LeaveReview = () => {
  const [formData, setFormData] = useState({
    reviewer_name: "",
    reviewer_email: "",
    relationship: "",
    care_type: "",
    stay_duration: "",
    rating: 0,
    review_title: "",
    review_text: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/reviews/submit`, formData);

      if (response.status === 201) {
        toast.success("Thank you for your review! We'll review and publish it shortly.");
        setSubmitted(true);
        // Reset form
        setFormData({
          reviewer_name: "",
          reviewer_email: "",
          relationship: "",
          care_type: "",
          stay_duration: "",
          rating: 0,
          review_title: "",
          review_text: ""
        });
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Something went wrong. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-12 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto border-2 border-green-200 bg-green-50">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="bg-green-100 text-green-600 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Thank You for Your Review!
              </h3>
              <p className="text-gray-700 mb-6">
                Your feedback is important to us. Our team will review your submission and it will appear on our website once approved.
              </p>
              <Button
                onClick={() => setSubmitted(false)}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                Submit Another Review
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-orange-500 text-white hover:bg-orange-600">
              Share Your Experience
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Leave a Review
            </h2>
            <p className="text-gray-600">
              Your feedback helps other families make informed decisions
            </p>
          </div>

          <Card className="border-2 border-teal-200 shadow-xl">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Rating */}
                <div>
                  <Label className="mb-2 block">Your Rating *</Label>
                  <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                    {[...Array(10)].map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleRatingClick(i + 1)}
                        className="focus:outline-none transition-transform hover:scale-110"
                      >
                        <Star
                          className={`h-6 w-6 sm:h-8 sm:w-8 ${
                            i < formData.rating
                              ? "fill-orange-500 text-orange-500"
                              : "text-gray-300"
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  <span className="mt-2 block text-lg font-semibold text-gray-700">
                    {formData.rating > 0 ? `${formData.rating}/10` : "Select rating"}
                  </span>
                </div>

                {/* Name */}
                <div>
                  <Label htmlFor="reviewer_name">Your Name *</Label>
                  <Input
                    id="reviewer_name"
                    value={formData.reviewer_name}
                    onChange={(e) => handleChange("reviewer_name", e.target.value)}
                    placeholder="John Smith"
                    required
                    className="mt-1"
                  />
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="reviewer_email">Email Address * (not displayed publicly)</Label>
                  <Input
                    id="reviewer_email"
                    type="email"
                    value={formData.reviewer_email}
                    onChange={(e) => handleChange("reviewer_email", e.target.value)}
                    placeholder="john.smith@example.com"
                    required
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Your email is only used for verification and will not be shown publicly</p>
                </div>

                {/* Relationship */}
                <div>
                  <Label htmlFor="relationship">Your Relationship *</Label>
                  <Select
                    value={formData.relationship}
                    onValueChange={(value) => handleChange("relationship", value)}
                    required
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select your relationship" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Daughter of resident">Daughter of resident</SelectItem>
                      <SelectItem value="Son of resident">Son of resident</SelectItem>
                      <SelectItem value="Wife of resident">Wife of resident</SelectItem>
                      <SelectItem value="Husband of resident">Husband of resident</SelectItem>
                      <SelectItem value="Family member">Family member</SelectItem>
                      <SelectItem value="Friend">Friend</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Care Type */}
                <div>
                  <Label htmlFor="care_type">Care Type Received *</Label>
                  <Select
                    value={formData.care_type}
                    onValueChange={(value) => handleChange("care_type", value)}
                    required
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select care type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Residential Care">Residential Care</SelectItem>
                      <SelectItem value="Nursing Care">Nursing Care</SelectItem>
                      <SelectItem value="Dementia Care">Dementia Care</SelectItem>
                      <SelectItem value="Respite Care">Respite Care</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Stay Duration */}
                <div>
                  <Label htmlFor="stay_duration">Length of Stay *</Label>
                  <Input
                    id="stay_duration"
                    value={formData.stay_duration}
                    onChange={(e) => handleChange("stay_duration", e.target.value)}
                    placeholder="e.g., 6 months, 2 years"
                    required
                    className="mt-1"
                  />
                </div>

                {/* Review Title */}
                <div>
                  <Label htmlFor="review_title">Review Title *</Label>
                  <Input
                    id="review_title"
                    value={formData.review_title}
                    onChange={(e) => handleChange("review_title", e.target.value)}
                    placeholder="Sum up your experience in one line"
                    required
                    className="mt-1"
                  />
                </div>

                {/* Review Text */}
                <div>
                  <Label htmlFor="review_text">Your Review *</Label>
                  <Textarea
                    id="review_text"
                    value={formData.review_text}
                    onChange={(e) => handleChange("review_text", e.target.value)}
                    placeholder="Share your experience with Barnston Court. What stood out? How was the care? How did the staff treat your loved one?"
                    rows={6}
                    required
                    className="mt-1"
                  />
                </div>

                {/* Privacy Notice */}
                <div className="bg-blue-50 border-l-4 border-l-blue-500 p-4 rounded">
                  <p className="text-sm text-gray-700">
                    <strong>Review Guidelines:</strong> Your review will be manually approved before appearing on our website. 
                    We reserve the right to not publish reviews that contain inappropriate language, false information, or violate our guidelines.
                  </p>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg"
                >
                  {isSubmitting ? (
                    "Submitting..."
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Submit Review
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
